import { useState } from 'react';
import { toast } from 'sonner';
import { Pencil } from 'lucide-react';
import api, { apiError } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { formatIDR, formatDate, methodLabel, PAY_METHODS } from '../lib/format';
import { MoneyInput } from './MoneyInput';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

export function PaymentListEditor({ payments, onChanged }) {
  // Koreksi pembayaran mengubah angka uang, jadi hanya owner yang boleh.
  // Sebelum 18 Okt 2026 tombolnya tetap tampil untuk karyawan lalu ditolak
  // server dengan "Khusus owner" — itulah "error" yang muncul tiap mau koreksi.
  const { user } = useAuth();
  const bolehKoreksi = user?.role !== 'karyawan';
  const [editPay, setEditPay] = useState(null);
  const [payForm, setPayForm] = useState({ amount: '', method: 'cash', date: '', kind: 'dp' });

  const openEdit = (p) => {
    setPayForm({ amount: p.amount, method: p.method, date: p.date, kind: p.kind || 'dp' });
    setEditPay(p);
  };

  const save = async () => {
    try {
      await api.put(`/payments/${editPay.id}`, { ...payForm, amount: Number(payForm.amount) });
      toast.success('Pembayaran diperbarui — total & sisa dihitung ulang otomatis.');
      setEditPay(null);
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const voidPay = async () => {
    try {
      await api.delete(`/payments/${editPay.id}`);
      toast.success('Pembayaran di-void (dihapus dari perhitungan, riwayat tetap ada).');
      setEditPay(null);
      onChanged();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <>
      <div className="space-y-2">
        {payments.map((p) => (
          <div key={p.id} data-testid={`editpay-row-${p.id.slice(0, 8)}`}
            className={`flex justify-between items-center text-sm bg-ivory rounded-xl px-4 py-2.5 ${p.voided ? 'opacity-40' : ''}`}>
            <span className={p.voided ? 'line-through' : ''}>
              <b className="text-brown">{p.kind === 'pelunasan' ? 'Pelunasan' : ['dp', 'deposit'].includes(p.kind) ? 'DP' : 'Pembayaran'}</b>
              {' '}• {methodLabel(p.method)} • {formatDate(p.date)}
              {p.voided && <span className="text-danger text-[10px] font-bold ml-1.5">VOID</span>}
            </span>
            <span className="flex items-center gap-1">
              <span className="font-semibold">{formatIDR(p.amount)}</span>
              {!p.voided && bolehKoreksi && (
                <button type="button" data-testid={`editpay-edit-${p.id.slice(0, 8)}`} onClick={() => openEdit(p)}
                  className="p-1.5 rounded-lg text-brown hover:bg-brown/10" title="Edit pembayaran">
                  <Pencil size={13} />
                </button>
              )}
            </span>
          </div>
        ))}
      </div>

      {!bolehKoreksi && payments.some((p) => !p.voided) && (
        <p className="text-[11px] text-mocha mt-2" data-testid="editpay-owner-only">
          Koreksi pembayaran hanya bisa lewat akun pemilik. Minta Owner yang membetulkan.
        </p>
      )}

      <Dialog open={!!editPay} onOpenChange={() => setEditPay(null)}>
        <DialogContent data-testid="editpay-dialog" className="bg-ivory max-w-sm">
          <DialogHeader><DialogTitle className="font-display text-brown-dark">Edit Pembayaran</DialogTitle></DialogHeader>
          <div className="space-y-2.5">
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Nominal (Rp)</label>
              <MoneyInput data-testid="editpay-amount" value={payForm.amount}
                onChange={(v) => setPayForm({ ...payForm, amount: v })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none focus:ring-2 focus:ring-brown/30" />
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Metode</label>
                <select data-testid="editpay-method" value={payForm.method}
                  onChange={(e) => setPayForm({ ...payForm, method: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none">
                  {PAY_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Jenis</label>
                <select data-testid="editpay-kind" value={payForm.kind}
                  onChange={(e) => setPayForm({ ...payForm, kind: e.target.value })}
                  className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none">
                  <option value="dp">DP</option>
                  <option value="pelunasan">Pelunasan</option>
                </select>
              </div>
            </div>
            <div>
              <label className="text-[11px] uppercase tracking-wider text-mocha font-semibold">Tanggal Pembayaran</label>
              <input data-testid="editpay-date" type="date" value={payForm.date}
                onChange={(e) => setPayForm({ ...payForm, date: e.target.value })}
                className="mt-1 w-full rounded-xl border border-input bg-white px-3 py-2.5 text-sm outline-none" />
            </div>
            <button data-testid="editpay-save" onClick={save}
              className="w-full rounded-xl bg-brown text-white py-3 text-sm font-bold hover:bg-brown-med">Simpan Perubahan</button>
            <button data-testid="editpay-void" onClick={voidPay}
              className="w-full rounded-xl border border-danger/30 text-danger py-2.5 text-xs font-bold hover:bg-danger/5">Void Pembayaran (salah catat)</button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
